import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  type ICognitoUserPoolData,
} from 'amazon-cognito-identity-js';
import { config, ensureCognitoConfigured } from './config.js';

let poolInstance: CognitoUserPool | null = null;

function getPool(): CognitoUserPool {
  if (poolInstance) return poolInstance;
  ensureCognitoConfigured();
  const data: ICognitoUserPoolData = {
    UserPoolId: config.cognitoUserPoolId,
    ClientId: config.cognitoClientId,
  };
  poolInstance = new CognitoUserPool(data);
  return poolInstance;
}

export interface CognitoSession {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  email: string;
  sub: string;
}

export async function cognitoSignUp(email: string, password: string): Promise<{ sub: string }> {
  const pool = getPool();
  return new Promise((resolve, reject) => {
    pool.signUp(
      email,
      password,
      [new CognitoUserAttribute({ Name: 'email', Value: email })],
      [],
      (err, result) => {
        if (err || !result) return reject(err ?? new Error('signup failed'));
        resolve({ sub: result.userSub });
      },
    );
  });
}

export async function cognitoSignIn(email: string, password: string): Promise<CognitoSession> {
  const pool = getPool();
  const user = new CognitoUser({ Username: email, Pool: pool });
  const auth = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(auth, {
      onSuccess: (session) => {
        const idToken = session.getIdToken();
        const payload = idToken.payload as { sub?: string; email?: string };
        if (!payload.sub || !payload.email) {
          reject(new Error('id token missing sub/email'));
          return;
        }
        resolve({
          idToken: idToken.getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
          email: payload.email,
          sub: payload.sub,
        });
      },
      onFailure: (err) => reject(err),
      newPasswordRequired: () => reject(new Error('new password required (unsupported in MVP)')),
    });
  });
}

export function cognitoSignOut(): void {
  const current = getPool().getCurrentUser();
  current?.signOut();
}
